import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, DialogContent, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
	addGpsrProductTypeRequest,
	getProductTypeReferencesRequest,
} from 'redux/reducers/gpsr/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { useParams, useLocation } from 'react-router';
import {
	getGpsrProductTypeReferences,
	getGpsrProductTypeReferencesLoad,
} from 'redux/reducers/gpsr/selectors';
import { TGetProductTypeReferences } from 'redux/reducers/gpsr/types';
import { validationSchema } from './validationSchema';
import { TAddProductTypeDialogProps } from './types';
import { ProductTypeReferencesAutocomplete } from './ProductTypeReferencesAutocomplete';

export const AddProductTypeDialog: FC<TAddProductTypeDialogProps> = ({
	open,
	setClose,
	emId,
	productTypeId = null,
	productTypeNameProps = '',
	productTypeDescriptionProps = '',
	productTypeIdProps = null,
}) => {
	const dispatch = useDispatch();
	const navigateParams = useLocation();
	const { page } = useParams();
	const params = useParams<{ emId?: string; productTypeId?: string }>();

	const productTypeReferences = useSelector(getGpsrProductTypeReferences);
	const productTypeReferencesLoading = useSelector(getGpsrProductTypeReferencesLoad);
	console.log('productTypeReferences', productTypeReferences);

	const [productTypeReferencesIndex, setProductTypeReferencesIndex] = useState<number | null>(null);

	const [productTypeName, setProductTypeName] = useState<string>(productTypeNameProps);
	const [productTypeDescription, setProductTypeDescription] = useState<string>(
		productTypeDescriptionProps,
	);

	useEffect(() => {
		if (open) {
			setProductTypeName(productTypeNameProps);
			setProductTypeDescription(productTypeDescriptionProps);
		}
	}, [productTypeNameProps, productTypeDescriptionProps, open]);

	useEffect(() => {
		dispatch(getProductTypeReferencesRequest({}));
	}, [dispatch]);

	const reset = () => {
		if (productTypeIdProps === null) {
			setProductTypeName('');
			setProductTypeDescription('');
			setProductTypeReferencesIndex(null);
		}
	};

	useEffect(() => {
		if (
			!open ||
			(productTypeReferencesIndex !== null &&
				productTypeIdProps === productTypeReferences?.data[productTypeReferencesIndex]?.id)
		)
			return;
		if (productTypeReferencesIndex !== null) {
			setProductTypeName(productTypeReferences?.data[productTypeReferencesIndex].typeName ?? '');
			setProductTypeDescription(
				productTypeReferences?.data[productTypeReferencesIndex].defaultDescription ?? '',
			);
		}
	}, [productTypeReferencesIndex, productTypeReferences, productTypeIdProps, open]);

	useEffect(() => {
		if (productTypeIdProps !== null) {
			setProductTypeReferencesIndex(
				productTypeReferences?.data.findIndex(item => item.id === productTypeIdProps) ?? null,
			);
		}
	}, [productTypeIdProps, productTypeReferences]);

	const formikGpsr = useFormik({
		initialValues: {
			productTypeName,
			productTypeDescription,
		},
		enableReinitialize: true,
		validationSchema,

		onSubmit: values => {
			if (!params.emId) return;
			dispatch(
				addGpsrProductTypeRequest({
					emId,

					body: {
						id: productTypeId,
						name: values.productTypeName,
						description: values.productTypeDescription,
						productTypeId:
							productTypeReferencesIndex === null
								? null
								: (productTypeReferences?.data[productTypeReferencesIndex]?.id ?? null),
					},
					request: {
						emId: +params.emId,
						pageNumber: page ? +page : 1,
						pageSize: PAGE_SIZE,
						searchString: String(navigateParams?.search ?? ''),
					},
				}),
			);
			formikGpsr.resetForm();
			reset();
			setClose();
			// setTimeout(() => {
			// 	window.location.reload();
			// }, 2000);
		},
	});

	return (
		<Dialog
			open={open}
			onClose={() => {
				setClose();
				reset();
			}}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Add Product Type</DialogTitle>
			<form onSubmit={formikGpsr.handleSubmit}>
				<DialogContent
					sx={{
						p: '0 12px 0px 12px',
					}}
				>
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
							p: '12px 0',
						}}
					>
						{productTypeReferences?.data && (
							<ProductTypeReferencesAutocomplete
								valueIndex={productTypeReferencesIndex}
								setValueIndex={setProductTypeReferencesIndex}
							/>
						)}
						{productTypeReferencesIndex !== null && (
							<>
								<TextField
									id="productTypeName"
									name="productTypeName"
									size="small"
									sx={{
										mt: '12px',
									}}
									label="Product Type Name"
									value={formikGpsr.values.productTypeName}
									onChange={formikGpsr.handleChange}
									onBlur={formikGpsr.handleBlur}
									type="text"
									fullWidth
									error={
										formikGpsr.touched.productTypeName && Boolean(formikGpsr.errors.productTypeName)
									}
									helperText={
										formikGpsr.touched.productTypeName && formikGpsr.errors.productTypeName
									}
								/>
								<TextField
									id="productTypeDescription"
									name="productTypeDescription"
									size="small"
									sx={{
										mt: '12px',
									}}
									label="Product Type Description"
									value={formikGpsr.values.productTypeDescription}
									onChange={formikGpsr.handleChange}
									onBlur={formikGpsr.handleBlur}
									type="text"
									fullWidth
									error={
										formikGpsr.touched.productTypeDescription &&
										Boolean(formikGpsr.errors.productTypeDescription)
									}
									helperText={
										formikGpsr.touched.productTypeDescription &&
										formikGpsr.errors.productTypeDescription
									}
								/>
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formikGpsr.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					{/* <Button
						type="button"
						onClick={handleParse}
						autoFocus
					>
						Parse
					</Button> */}

					<Button
						type="submit"
						// onClick={() => {
						// 	console.log('Button send clicked');
						// 	formikGpsr.handleSubmit();
						// }}
						autoFocus
						disabled={
							formikGpsr.values.productTypeName === '' ||
							formikGpsr.values.productTypeDescription === '' ||
							productTypeReferencesIndex === null
						}
					>
						Send
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
